import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Select } from "antd";
import {
  getDarRoleTypes,
  getDarRoleCurrentAssignments,
  assignDarRole,
  getDarRoleUserStatus,
  getDarDepartments,
  getDarNodalList,
  getDarHrAuditorList,
} from "../../../core/data/redux/darSlice";
import { AppDispatch } from "../../../core/data/redux/store";

const normalizeOption = (opt: any) => {
  if (opt && typeof opt === "object") {
    const value = opt.value ?? opt.role_type ?? opt.name ?? opt.id ?? opt.code;
    const label = opt.label ?? opt.role_type ?? opt.name ?? opt.id ?? opt.code;
    return { value, label };
  }
  return { value: opt, label: opt };
};

const toArray = (res: any): any[] => {
  const candidate = res?.data?.data ?? res?.data ?? res;
  return Array.isArray(candidate) ? candidate : [];
};

const toDepartmentsArray = (res: any): any[] => {
  const candidate = res?.departments ?? res?.data?.departments ?? res?.data?.data?.departments ?? [];
  return Array.isArray(candidate) ? candidate : [];
};

const ROLE_FLAG_LABELS: Record<string, string> = {
  is_hod: "HOD",
  is_nodal: "Nodal Officer",
  is_auditor: "HR Auditor",
};

const roleFlagLabel = (key: string) => {
  if (ROLE_FLAG_LABELS[key]) return ROLE_FLAG_LABELS[key];
  return key
    .replace(/^is_/i, "")
    .split("_")
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const DarRoleManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [roleTypes, setRoleTypes] = useState<any[]>([]);
  const [loadingRoleTypes, setLoadingRoleTypes] = useState(false);

  const [departments, setDepartments] = useState<any[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const [userStatus, setUserStatus] = useState<any | null>(null);
  const [loadingUserStatus, setLoadingUserStatus] = useState(false);

  const [assignForm, setAssignForm] = useState({
    target_empno: "",
    department_id: "",
    role_type: "",
  });
  const [assigning, setAssigning] = useState(false);

  const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeeFetchError, setEmployeeFetchError] = useState<string | null>(null);

  const [searchDeptId, setSearchDeptId] = useState("");
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assignmentsFetched, setAssignmentsFetched] = useState(false);

  const findArrayCandidate = (res: any): any[] => {
    if (!res) return [];
    if (Array.isArray(res)) return res;

    const explicitPaths = [
      res?.data?.data?.hr_auditors,
      res?.data?.data?.hr_auditor_list,
      res?.data?.data?.auditors,
      res?.data?.data?.auditor_list,
      res?.data?.data?.nodals,
      res?.data?.data?.nodal_officers,
      res?.data?.data?.nodal_list,
      res?.data?.data?.employees,
      res?.data?.data?.list,
      res?.data?.data?.users,

      res?.data?.hr_auditors,
      res?.data?.hr_auditor_list,
      res?.data?.auditors,
      res?.data?.auditor_list,
      res?.data?.nodals,
      res?.data?.nodal_officers,
      res?.data?.nodal_list,
      res?.data?.employees,
      res?.data?.list,
      res?.data?.users,

      res?.hr_auditors,
      res?.hr_auditor_list,
      res?.auditors,
      res?.auditor_list,
      res?.nodals,
      res?.nodal_officers,
      res?.nodal_list,
      res?.employees,
      res?.list,
      res?.result,
    ];

    for (const p of explicitPaths) {
      if (Array.isArray(p) && p.length > 0) {
        return p;
      }
    }

    const searchDeep = (obj: any): any[] | null => {
      if (!obj || typeof obj !== "object") return null;
      if (Array.isArray(obj)) return obj;
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (Array.isArray(val) && val.length > 0) {
          return val;
        }
        if (val && typeof val === "object") {
          const found = searchDeep(val);
          if (found && found.length > 0) return found;
        }
      }
      return null;
    };

    const deepResult = searchDeep(res);
    if (deepResult) return deepResult;

    for (const p of explicitPaths) {
      if (Array.isArray(p)) return p;
    }

    return [];
  };

  const getLoggedInEmpNo = (statusObj?: any): string => {
    const status = statusObj || userStatus;
    if (status) {
      const fromStatus = status.emp_no || status.empNo || status.employee_number || status.emp_id || status.empId || status.id;
      if (fromStatus) return String(fromStatus).trim();
    }
    try {
      const rawUser = localStorage.getItem("user") || localStorage.getItem("userData") || localStorage.getItem("userInfo");
      if (rawUser && rawUser !== "undefined") {
        const parsed = JSON.parse(rawUser);
        const val = parsed.emp_no || parsed.empNo || parsed.emp_id || parsed.empId || parsed.employeeNumber || parsed.employee_number || parsed.id;
        if (val) return String(val).trim();
      }
    } catch (e) { }
    return "";
  };

  const parseEmployeeOptions = (res: any, loggedInEmpNo: string) => {
    const list = findArrayCandidate(res);

    return list
      .map((item: any) => {
        if (item === null || item === undefined) return null;

        if (typeof item === "string" || typeof item === "number") {
          const strVal = String(item).trim();
          if (!strVal) return null;
          return {
            value: strVal,
            label: strVal,
            emp_no: strVal,
            emp_name: "",
          };
        }

        const empNo = String(
          item.emp_no ??
          item.empno ??
          item.emp_id ??
          item.empId ??
          item.employee_number ??
          item.employee_no ??
          item.target_empno ??
          item.id ??
          item.value ??
          item.code ??
          ""
        ).trim();

        const empName = String(
          item.emp_name ??
          item.empname ??
          item.name ??
          item.employee_name ??
          item.empName ??
          item.full_name ??
          item.label ??
          ""
        ).trim();

        if (!empNo && !empName) return null;

        const finalVal = empNo || empName;
        const label = empName && empNo && empName !== empNo
          ? `${empName} - ${empNo}`
          : empName || empNo;

        return {
          value: finalVal,
          label: label,
          emp_no: empNo,
          emp_name: empName,
        };
      })
      .filter((opt: any) => {
        if (!opt || !opt.value) return false;
        if (loggedInEmpNo && opt.value.toLowerCase() === loggedInEmpNo.toLowerCase()) {
          return false;
        }
        return true;
      });
  };

  const fetchEmployeeOptions = (roleType: string, loggedInEmpNo?: string, deptId?: string) => {
    if (!roleType) {
      setEmployeeOptions([]);
      setEmployeeFetchError(null);
      return;
    }

    setLoadingEmployees(true);
    setEmployeeFetchError(null);

    const empNo = loggedInEmpNo || getLoggedInEmpNo();
    const departmentId = deptId || assignForm.department_id || userStatus?.department_id || "";

    const isNodal = /nodal/i.test(roleType);
    const isHrAuditor = /auditor/i.test(roleType);

    const payload: any = { emp_no: empNo };
    if (departmentId) {
      payload.department_id = departmentId;
    }

    let actionToDispatch: any = null;

    if (isNodal) {
      actionToDispatch = getDarNodalList(payload);
    } else if (isHrAuditor) {
      actionToDispatch = getDarHrAuditorList(payload);
    }

    if (!actionToDispatch) {
      setLoadingEmployees(false);
      setEmployeeOptions([]);
      return;
    }

    dispatch(actionToDispatch)
      .unwrap()
      .then((res: any) => {
        const options = parseEmployeeOptions(res, empNo);
        setEmployeeOptions(options);
      })
      .catch((err: any) => {
        const msg = typeof err === "string" ? err : (err?.message || "Failed to fetch employee list.");
        setEmployeeFetchError(msg);
        toast.error(msg);
        setEmployeeOptions([]);
      })
      .finally(() => {
        setLoadingEmployees(false);
      });
  };

  const handleRoleTypeChange = (newRoleType: string) => {
    setAssignForm(prev => ({
      ...prev,
      role_type: newRoleType,
      target_empno: "",
    }));
    fetchEmployeeOptions(newRoleType);
  };

  const fetchRoleTypes = () => {
    setLoadingRoleTypes(true);
    dispatch(getDarRoleTypes({}))
      .unwrap()
      .then((res: any) => setRoleTypes(toArray(res)))
      .catch(() => {
        // Fall back to static options below if the endpoint isn't available yet.
      })
      .finally(() => setLoadingRoleTypes(false));
  };

  const fetchDepartments = () => {
    setLoadingDepartments(true);
    dispatch(getDarDepartments({}))
      .unwrap()
      .then((res: any) => setDepartments(toDepartmentsArray(res)))
      .catch((err: any) => {
        toast.error(typeof err === "string" ? err : (err?.message || "Failed to fetch departments."));
      })
      .finally(() => setLoadingDepartments(false));
  };

  const fetchUserStatus = () => {
    setLoadingUserStatus(true);
    dispatch(getDarRoleUserStatus({}))
      .unwrap()
      .then((res: any) => {
        const data = res?.data ?? res;
        setUserStatus(data && typeof data === "object" ? data : null);
      })
      .catch((err: any) => {
        toast.error(typeof err === "string" ? err : (err?.message || "Failed to fetch your role status."));
      })
      .finally(() => setLoadingUserStatus(false));
  };

  useEffect(() => {
    fetchRoleTypes();
    fetchDepartments();
    fetchUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Assign Role is scoped to the logged-in user's own department — take it
  // directly from the user-status response rather than letting them pick.
  useEffect(() => {
    if (userStatus?.department_id) {
      setAssignForm(prev => ({ ...prev, department_id: userStatus.department_id }));
    }
  }, [userStatus]);

  const fetchAssignments = async (deptId: string) => {
    if (!deptId.trim()) {
      toast.warning("Please enter a Department ID.");
      return;
    }
    setLoadingAssignments(true);
    try {
      const res = await dispatch(getDarRoleCurrentAssignments({ department_id: deptId.trim() })).unwrap();
      setAssignments(toArray(res));
      setAssignmentsFetched(true);
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : (err?.message || "Failed to fetch current assignments."));
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignForm.target_empno.trim() || !assignForm.department_id.trim() || !assignForm.role_type) {
      toast.warning("Please select Employee No., Department, and Role Type.");
      return;
    }
    setAssigning(true);
    try {
      const res = await dispatch(assignDarRole({
        target_empno: assignForm.target_empno.trim(),
        department_id: assignForm.department_id.trim(),
        role_type: assignForm.role_type,
      })).unwrap();
      const status = res?.status || res?.data?.status;
      if (status === 200 || status === 201) {
        toast.success(res?.message || res?.data?.message || "Role assigned successfully.");
        setAssignForm(prev => ({ ...prev, target_empno: "", role_type: "" }));
        setEmployeeOptions([]);
        if (searchDeptId.trim() && searchDeptId.trim() === assignForm.department_id.trim()) {
          fetchAssignments(searchDeptId);
        }
      } else {
        toast.error(res?.message || res?.data?.message || "Failed to assign role.");
      }
    } catch (err: any) {
      toast.error(typeof err === "string" ? err : (err?.message || "Error assigning role."));
    } finally {
      setAssigning(false);
    }
  };

  const roleTypeOptions = roleTypes.length > 0
    ? roleTypes.map(normalizeOption)
    : [
        { value: "NODAL", label: "NODAL" },
        { value: "HR_AUDITOR", label: "HR_AUDITOR" },
      ];

  const departmentOptions = departments.map((dept: any) => ({
    value: dept.department_id,
    label: dept.department_name ? `${dept.department_name} (${dept.department_id})` : dept.department_id,
  }));

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">D&AR Role Management</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/dar-cases/overview">D&AR Cases</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Role Management</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* My Role Status */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white border-bottom p-4 d-flex justify-content-between align-items-center">
            <h5 className="mb-0 text-primary d-flex align-items-center">
              <i className="ti ti-user-check fs-3 me-2" style={{ fontSize: '1.5rem' }}></i> My D&AR Role Status
            </h5>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchUserStatus} disabled={loadingUserStatus}>
              <i className="ti ti-refresh me-1"></i> Refresh
            </button>
          </div>
          <div className="card-body p-4">
            {loadingUserStatus ? (
              <div className="d-flex align-items-center gap-2 text-muted">
                <span className="spinner-border spinner-border-sm"></span> Loading your role status...
              </div>
            ) : userStatus ? (
              (() => {
                const activeRoleKeys = Object.keys(userStatus).filter(k => /^is_/i.test(k) && userStatus[k] === true);
                return (
                  <div className="border rounded p-3">
                    <div className="text-muted small mb-1">Department</div>
                    <div className="fw-semibold text-dark mb-3">
                      {userStatus.department_name || userStatus.department_id || "-"}
                    </div>
                    <div className="text-muted small mb-2">Roles</div>
                    {activeRoleKeys.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {activeRoleKeys.map(key => (
                          <span key={key} className="badge bg-success">{roleFlagLabel(key)}</span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted fst-italic small">No D&AR roles are currently assigned to you.</div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div className="text-muted fst-italic">No D&AR roles are currently assigned to you.</div>
            )}
          </div>
        </div>

        {/* Assign Role */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white border-bottom p-4">
            <h5 className="mb-0 text-primary d-flex align-items-center">
              <i className="ti ti-user-plus fs-3 me-2" style={{ fontSize: '1.5rem' }}></i> Assign Role
            </h5>
          </div>
          <form onSubmit={handleAssignSubmit}>
            <div className="card-body p-4">
              <div className="row g-4">
                {/* 1. Role Type (Column 1) */}
                <div className="col-md-4">
                  <label className="form-label">Role Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={assignForm.role_type}
                    onChange={e => handleRoleTypeChange(e.target.value)}
                    disabled={loadingRoleTypes}
                    required
                  >
                    <option value="">{loadingRoleTypes ? "Loading..." : "Select Role Type"}</option>
                    {roleTypeOptions.map((opt: any, idx: number) => (
                      <option key={idx} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* 2. Department (Column 2) */}
                <div className="col-md-4">
                  <label className="form-label">Department <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      loadingUserStatus
                        ? "Loading..."
                        : userStatus?.department_name
                          ? `${userStatus.department_name} (${assignForm.department_id})`
                          : assignForm.department_id || "-"
                    }
                    readOnly
                  />
                </div>

                {/* 3. Employee No. (Column 3) */}
                <div className="col-md-4">
                  <label className="form-label">Employee No. <span className="text-danger">*</span></label>
                  <Select
                    className="w-100"
                    size="large"
                    showSearch
                    allowClear
                    disabled={!assignForm.role_type || loadingEmployees}
                    placeholder={
                      !assignForm.role_type
                        ? "Select Role Type first"
                        : loadingEmployees
                          ? "Loading employees..."
                          : employeeFetchError
                            ? "Error loading employees"
                            : "Search by Emp No. or Name"
                    }
                    loading={loadingEmployees}
                    value={assignForm.target_empno || undefined}
                    onChange={(val: string) => setAssignForm(prev => ({ ...prev, target_empno: val || "" }))}
                    options={employeeOptions}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    notFoundContent={
                      loadingEmployees
                        ? "Loading..."
                        : employeeFetchError
                          ? <span className="text-danger">{employeeFetchError}</span>
                          : !assignForm.role_type
                            ? "Please select a Role Type first."
                            : "No eligible employees found in your department."
                    }
                  />
                  {employeeFetchError && (
                    <div className="text-danger small mt-1 d-flex align-items-center justify-content-between">
                      <span>Failed to load employees.</span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none ms-1"
                        onClick={() => fetchEmployeeOptions(assignForm.role_type, getLoggedInEmpNo(), assignForm.department_id)}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="card-footer bg-white border-top p-3 text-end">
              <button
                type="submit"
                className="btn btn-warning px-4 text-dark"
                disabled={assigning || !assignForm.target_empno || !assignForm.department_id || !assignForm.role_type || loadingEmployees}
              >
                {assigning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span> Assigning...
                  </>
                ) : (
                  <>
                    <i className="ti ti-shield-plus me-2"></i> Assign Role
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Current Assignments */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white border-bottom p-4">
            <h5 className="mb-0 text-primary d-flex align-items-center">
              <i className="ti ti-list-details fs-3 me-2" style={{ fontSize: '1.5rem' }}></i> Current Role Assignments
            </h5>
          </div>
          <div className="card-body p-4">
            <div className="d-flex gap-3 align-items-end mb-4">
              <div className="flex-grow-1" style={{ maxWidth: 360 }}>
                <label className="form-label">Department ID</label>
                <Select
                  className="w-100"
                  size="large"
                  showSearch
                  allowClear
                  placeholder={loadingDepartments ? "Loading departments..." : "Select Department"}
                  loading={loadingDepartments}
                  value={searchDeptId || undefined}
                  onChange={(val: string) => setSearchDeptId(val || "")}
                  options={departmentOptions}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingDepartments ? "Loading..." : "No departments found"}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary px-4 d-flex align-items-center"
                onClick={() => fetchAssignments(searchDeptId)}
                disabled={loadingAssignments}
              >
                {loadingAssignments ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="ti ti-search me-2"></i>}
                Load Assignments
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Employee No.</th>
                    <th>Employee Name</th>
                    <th>Role Type</th>
                    <th>Department</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.length > 0 ? (
                    assignments.map((row: any, idx: number) => (
                      <tr key={idx}>
                        <td>{row.target_empno || row.empno || row.emp_no || "-"}</td>
                        <td>{row.empname || row.emp_name || row.name || "-"}</td>
                        <td><span className="fw-medium">{row.role_type || row.role || "-"}</span></td>
                        <td>{row.department_id || row.department || "-"}</td>
                        <td>
                          <span className={`badge ${String(row.status || "").toLowerCase() === "active" ? "bg-success" : "bg-light text-dark border"}`}>
                            {row.status || "-"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        {assignmentsFetched ? "No role assignments found for this department." : "Enter a Department ID and click Load Assignments."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DarRoleManagement;
